/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React, { ReactElement, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { ReactNode } from "react";

import CCheckRadio from "../CCheckRadio/CCheckRadio";

import CAutocomplete from "../CAutocomplete/CAutocomplete";
import { Input, Select, TextArea } from "../index";

export const dynamic = "force-dynamic";

// Supported Field Types
export type FieldType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "textarea"
  | "checkbox"
  | "select"
  | "autocomplete"
  | "date"
  | "time"
  | "file"
  | "url"
  | "tel"
  | "datetime-local";

interface FieldOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean; // NEW - to disable specific options
}

export interface FieldConfig {
  type: FieldType;
  name: string;
  label: React.ReactNode;
  placeholder?: string;
  options?: FieldOption[]; // For select
  accept?: string; // For file
  multiple?: boolean;
  required?: boolean;
  colSpan?: number; // NEW — controls how many grid columns the field spans
  min?: number; // For number inputs
  max?: number; // For number inputs
  step?: number; // For number inputs
  rows?: number; // For textarea
  disabled?: boolean; // NEW — disables the field if true
  searchable?: boolean; // For select
  maxLength?: number; // For text inputs
  dnd?: boolean; // Enable drag and drop for file inputs
  height?: string; // Custom height for file input labels (e.g., "h-20", "h-32")
  // NEW - Dynamic label support
  dynamicLabel?: (
    value: string | number | boolean | undefined,
  ) => string | React.ReactNode;
  hint?: string | React.ReactNode; // Static hint text
  dynamicHint?: (
    value: string | number | boolean | undefined,
  ) => string | React.ReactNode; // Dynamic hint based on value
  defaultValue?: string | number | boolean; // Default value for the field
  isShow?: boolean; // NEW - to conditionally show/hide the field (default: true)
  maxDate?: string; // For date inputs
  minDate?: string; // For date inputs
  // For autocomplete field type
  onFetchOptions?: (query: string) => Promise<FieldOption[]>; // Async function to fetch options
  minChars?: number; // Minimum characters before triggering search (autocomplete)
  debounceMs?: number; // Debounce delay in milliseconds (autocomplete)
  maxheight?: string; // Custom max-height for select dropdown (e.g., "max-h-60")
  divider?: boolean; // NEW - adds a divider before the field
  endIcon?: React.ReactNode; // NEW - icons at the end of the input
}

export interface ButtonAction {
  title: string;
  action?: () => void;
  icon?: ReactElement;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "icon";
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
  customClass?: string;
  fontSize?: string;
  fontStyle?: string;
  gap?: string;
  textUpperCased?: boolean;
  color?: string;
  btnTitle?: string | React.ReactNode;
  circle?: boolean;
  paddingNone?: boolean;
  id?: string;
  [key: string]: any;
  size?: "sm" | "lg" | "icon" | "default"; // NEW - button size
}

interface DynamicFormProps {
  fields: FieldConfig[];
  onSubmit?: (data: any) => void | Promise<void>;
  errors?: Record<string, string[]>; // Field-specific errors
  fieldErrors?: Record<string, string[]>; // Alias for errors, for compatibility
  initialValues?: Record<string, unknown>; // Initial values for form fields
  actions?: Array<ButtonAction>; // Button actions
  columns?: 1 | 2 | 3 | 4 | 5 | 6; // Tailwind supports up to 12
  values?: Record<string, any>; // Controlled values from parent
  onChange?: (field: string, value: any) => void; // Controlled change handler
  children?: React.ReactNode; // NEW - to allow custom children
  /**
   * When true and the field is disabled, render a compact read-only text
   * representation instead of a disabled input. Useful for consistent
   * view/edit rendering. Default: false (preserves inputs).
   */
  readOnlyAsText?: boolean;
  /**
   * Placeholder to show when a value is empty in read-only text mode.
   */
  emptyPlaceholder?: string;
}

const RenderField = ({
  field,
  value,
  onChange,
  onChangeValue,
  error,
  validationError,
  readOnlyAsText,
  emptyPlaceholder = "-",
}: {
  field: FieldConfig;
  value: string | number | boolean | File | FileList | null | undefined;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  onChangeValue?: (value: string | number) => void;
  error?: string[];
  validationError?: string;
  readOnlyAsText?: boolean;
  emptyPlaceholder?: string;
}) => {
  // State for drag and drop
  const [isDragOver, setIsDragOver] = useState(false);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!field.disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (field.disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {
        name: field.name,
        type: "file",
        files: field.multiple ? e.dataTransfer.files : e.dataTransfer.files,
        multiple: field.multiple,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  // Helper to render label with required asterisk
  const renderLabel = (
    label: React.ReactNode,
    required?: boolean,
  ): ReactNode => (
    <span className="flex items-center ">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  const hasError = error && error.length > 0;
  const errorClass = hasError ? "border-red-500 focus:border-red-500" : "";

  // Generate dynamic label if dynamicLabel function is provided
  const getEffectiveLabel = (): React.ReactNode => {
    if (field.dynamicLabel && typeof field.dynamicLabel === "function") {
      // Convert value to basic types for dynamic label function
      const dynamicValue =
        value instanceof File || value instanceof FileList || value === null
          ? undefined
          : value;
      return field.dynamicLabel(dynamicValue);
    }
    return field.label;
  };

  // Generate dynamic hint if dynamicHint function is provided
  const getEffectiveHint = (): React.ReactNode | null => {
    if (field.dynamicHint && typeof field.dynamicHint === "function") {
      // Convert value to basic types for dynamic hint function
      const dynamicValue =
        value instanceof File || value instanceof FileList || value === null
          ? undefined
          : value;
      return field.dynamicHint(dynamicValue);
    }
    return field.hint || null;
  };

  const renderFieldComponent = () => {
    // When requested, render a compact read-only text representation for disabled fields
    if (field.disabled && readOnlyAsText !== false) {
      let display: React.ReactNode = emptyPlaceholder;
      if (value instanceof File) display = value.name;
      else if (value instanceof FileList) display = `${value.length} files`;
      else if (typeof value === "boolean") display = value ? "Yes" : "No";
      else if (typeof value === "number") display = String(value);
      else if (typeof value === "string" && value.trim() !== "") {
        // For select fields, prefer the option label when available
        if (field.type === "select" && field.options) {
          const found = field.options.find((o) => o.value === value);
          display = found ? found.label : value;
        } else {
          display = value;
        }
      }

      return (
        <div>
          <span className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
            {renderLabel(getEffectiveLabel(), field.required)}
          </span>
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {display}
          </p>
          {getEffectiveHint() && (
            <div className="mt-1">
              <p className="text-sm italic text-gray-600">
                {getEffectiveHint()}
              </p>
            </div>
          )}
        </div>
      );
    }

    let inputValue: string = "";
    // Ensure value is always a string for CInput/CTextArea to avoid cursor jump
    if (typeof value === "boolean") {
      inputValue = value ? "true" : "false";
    } else if (typeof value === "number") {
      inputValue = String(value);
    } else if (typeof value === "string") {
      inputValue = value;
    }

    switch (field.type) {
      case "textarea":
        return (
          <TextArea
            name={field.name}
            label={
              renderLabel(getEffectiveLabel(), field.required) as ReactNode
            }
            placeholder={field.placeholder}
            onChange={onChange}
            value={inputValue}
            className={`border p-2 rounded w-full ${errorClass}`}
            required={field.required}
            rows={field.rows}
            disabled={field.disabled}
          />
        );

      case "select":
        return (
          <>
            <Select
              name={field.name}
              label={
                renderLabel(getEffectiveLabel(), field.required) as ReactNode
              }
              placeholder={field.placeholder}
              value={inputValue}
              onChange={onChangeValue}
              options={field.options || []}
              required={field.required}
              className={errorClass}
              disabled={field.disabled}
              searchable={field.searchable}
              errorQuery={hasError}
              errorMessage={error?.[0] || validationError}
              maxHeight={field.maxheight}
            />
          </>
        );

      case "autocomplete":
        if (!field.onFetchOptions) {
          console.error(
            `Field "${field.name}" is type "autocomplete" but missing onFetchOptions function`,
          );
          return null;
        }
        return (
          <CAutocomplete
            name={field.name}
            label={
              renderLabel(getEffectiveLabel(), field.required) as ReactNode
            }
            placeholder={field.placeholder}
            value={inputValue}
            onChange={onChangeValue}
            onFetchOptions={field.onFetchOptions}
            required={field.required}
            disabled={field.disabled}
            minChars={field.minChars}
            debounceMs={field.debounceMs}
            errorQuery={hasError}
            errorMessage={error?.[0]}
          />
        );

      case "checkbox":
        // Render label with red asterisk for required fields
        let labelString = "";
        const effectiveLabel = getEffectiveLabel();
        if (typeof effectiveLabel === "string") {
          labelString = effectiveLabel;
        }
        return (
          <div className="flex items-center gap-2">
            <CCheckRadio
              type="checkbox"
              name={field.name}
              id={field.name}
              checked={!!value}
              onChange={(e) => e && onChange(e)}
              label={labelString}
              disabled={field.disabled}
            />
            {field.required && <span className="text-red-500">*</span>}
          </div>
        );

      case "file":
        // Determine if DnD should be enabled
        const enableDnD = field.dnd !== false;

        return (
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-500 mb-1">
              {renderLabel(getEffectiveLabel(), field.required)}
            </span>
            <div className="relative">
              <input
                type="file"
                name={field.name}
                onChange={onChange}
                className="sr-only"
                accept={field.accept}
                multiple={field.multiple}
                disabled={field.disabled}
                id={`file-${field.name}`}
              />
              <label
                htmlFor={`file-${field.name}`}
                className={`
                  flex items-center justify-center w-full ${!value ? field.height : "h-10"} border border-solid rounded cursor-pointer
                  transition-all duration-150 ease-in-out text-xs
                  ${
                    enableDnD && isDragOver
                      ? "bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-500"
                      : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                  }
                  ${errorClass}
                  ${field.disabled ? "opacity-50 cursor-not-allowed" : ""}
                `}
                onDragOver={enableDnD ? handleDragOver : undefined}
                onDragLeave={enableDnD ? handleDragLeave : undefined}
                onDrop={enableDnD ? handleDrop : undefined}
              >
                {!value ? (
                  <div className="flex items-center gap-2 px-3 py-2">
                    <svg
                      className={`w-4 h-4 shrink-0 ${
                        enableDnD && isDragOver
                          ? "text-blue-500 dark:text-blue-400"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <span
                      className={`text-xs ${
                        enableDnD && isDragOver
                          ? "text-blue-600 dark:text-blue-400 font-medium"
                          : "text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {enableDnD && isDragOver
                        ? "Drop here"
                        : enableDnD
                          ? "Click or drag file"
                          : "Click to select"}
                    </span>
                    {field.accept && (
                      <span className="text-xs text-gray-400">
                        ({field.accept.split("/")[1] || field.accept})
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg
                        className="w-3 h-3 text-blue-500 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                        {value instanceof File
                          ? value.name
                          : value instanceof FileList
                            ? `${value.length} files`
                            : "File selected"}
                      </span>
                      {value instanceof File && (
                        <span className="text-xs text-gray-400">
                          ({(value.size / 1024).toFixed(0)}KB)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        if (typeof onChange === "function") {
                          const syntheticEvent = {
                            target: {
                              name: field.name,
                              type: "file",
                              files: null,
                              multiple: field.multiple,
                            },
                          } as React.ChangeEvent<HTMLInputElement>;
                          onChange(syntheticEvent);
                        }
                      }}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </label>
            </div>
          </div>
        );

      case "date":
      case "datetime-local":
        // Render CInput with no endIcon for date/datetime-local
        return (
          <Input
            type={field.type}
            name={field.name}
            label={
              renderLabel(getEffectiveLabel(), field.required) as ReactNode
            }
            placeholder={field.placeholder}
            value={inputValue}
            onChange={onChange}
            required={field.required}
            className={errorClass}
            min={field.minDate}
            max={field.maxDate}
            step={field.step}
            disabled={field.disabled}
            // No endIcon for date/datetime-local
          />
        );

      default:
        // Use CInput for text-like fields
        return (
          <Input
            type={field.type}
            name={field.name}
            label={
              renderLabel(getEffectiveLabel(), field.required) as ReactNode
            }
            placeholder={field.placeholder}
            value={inputValue}
            onChange={onChange}
            required={field.required}
            className={errorClass}
            min={field.min}
            max={field.max}
            step={field.step}
            maxLength={field.maxLength}
            disabled={field.disabled}
            endIcon={field.endIcon}
          />
        );
    }
  };

  return (
    <div>
      {renderFieldComponent()}
      {/* Display dynamic or static hint */}
      {getEffectiveHint() && (
        <div className="mt-1">
          <p
            className={`text-sm italic ${
              field.name === "router_ip" &&
              getEffectiveHint() === "Invalid IP address format."
                ? "text-red-600"
                : "text-gray-600"
            }`}
          >
            {getEffectiveHint()}
          </p>
        </div>
      )}
      {/* Display errors */}
      {hasError && (
        <div className="mt-1">
          {error.map((errorMsg, index) => (
            <p key={index} className="text-sm text-red-600">
              {errorMsg}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  onSubmit,
  errors,
  fieldErrors,
  initialValues = {},
  actions = [],
  columns = 1,
  values,
  onChange,
  children,
  readOnlyAsText,
  emptyPlaceholder = "-",
}) => {
  // Prefer fieldErrors if provided, else errors, else empty object
  const mergedErrors = fieldErrors || errors || {};
  // Internal state only if not controlled
  const [formData, setFormData] = useState<Record<string, any>>({});
  // Add validation state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Initialize/reinitialize form data when fields or initialValues change (uncontrolled only)
  useEffect(() => {
    if (values) return; // skip if controlled
    const initialData: Record<string, unknown> = {};
    fields.forEach((field) => {
      if (field.type === "checkbox") {
        initialData[field.name] = false;
      } else {
        // Initialize all fields as empty string to avoid uncontrolled to controlled warnings
        initialData[field.name] = "";
      }
    });
    if (initialValues && typeof initialValues === "object") {
      Object.keys(initialValues).forEach((key) => {
        if (initialValues[key] !== undefined) {
          initialData[key] = initialValues[key];
        }
      });
    } else {
      // If no initialValues provided, use defaultValue from field config
      fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          // console.log(
          //   `Setting defaultValue for ${field.name}:`,
          //   field.defaultValue,
          // );
          initialData[field.name] = field.defaultValue;
        }
      });
    }
    setFormData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, values]);

  // Controlled or uncontrolled value
  const currentValues = values || formData;

  // Controlled or uncontrolled change handler
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, type, value, checked, files, multiple } =
      e.target as HTMLInputElement;
    const newValue =
      type === "checkbox"
        ? checked
        : type === "file"
          ? multiple
            ? files
            : files?.[0]
          : value;
    if (onChange) {
      onChange(name, newValue);
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    }
  };

  // For CSelect (Headless UI), pass value directly
  const handleChangeValue = (fieldName: string, value: string | number) => {
    // Clear validation error when field changes
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    if (onChange) {
      onChange(fieldName, value);
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    //Check required fields and show custom validation for select fields
    const newValidationErrors: Record<string, string> = {};
    const visibleRequiredFields = visibleFields.filter(
      (field) => field.required,
    );

    for (const field of visibleRequiredFields) {
      const value = currentValues[field.name];
      const isEmpty =
        !value || (typeof value === "string" && value.trim() === "");

      if (isEmpty) {
        if (field.type === "select") {
          newValidationErrors[field.name] = "Please fill out this field.";
        }
      }
    }

    setValidationErrors(newValidationErrors);

    // If there are validation errors, don't submit
    if (Object.keys(newValidationErrors).length > 0) {
      return;
    }

    // Clear validation errors on successful submission
    setValidationErrors({});

    try {
      await onSubmit?.(currentValues);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleReset = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (onChange) {
      // Reset all fields to empty string
      fields.forEach((field) => onChange(field.name, ""));
    } else {
      setFormData({});
    }
  };

  const columnClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-1 md:grid-cols-2 lg:grid-cols-5",
    6: "grid-cols-1 md:grid-cols-2 lg:grid-cols-6",
  }[columns || 1];

  // Filter fields to only show those with isShow = true (default true)
  const visibleFields = fields.filter((field) => field.isShow !== false);

  return (
    <form
      onSubmit={handleSubmit}
      onReset={handleReset}
      className={`grid gap-2 ${columnClass}`}
    >
      {visibleFields.map((field) => {
        const span = field.colSpan || 1;
        // If colSpan matches columns, use col-span-full for desktop
        let responsiveSpanClass = "col-span-1";
        if (span === columns) {
          responsiveSpanClass = `col-span-1 md:col-span-full`;
        } else if (span > 1) {
          responsiveSpanClass = `col-span-1 md:col-span-${Math.min(
            span,
            columns || 1,
          )}`;
        }
        return (
          <div key={field.name} className={responsiveSpanClass}>
            <RenderField
              field={field}
              value={currentValues[field.name]}
              onChange={handleChange}
              onChangeValue={
                field.type === "select" || field.type === "autocomplete"
                  ? (val) => handleChangeValue(field.name, val)
                  : undefined
              }
              error={mergedErrors[field.name]}
              validationError={validationErrors[field.name]}
            />
          </div>
        );
      })}
      {children}
      <section className="col-span-full">
        {actions?.length > 0 && (
          <div className="flex justify-end space-x-2 mt-4">
            {actions?.map((action, index) => {
              const {
                action: actionFn,
                title,
                icon,
                type,
                variant,
                loading,
                disabled,
                ...rest
              } = action;
              // If type is reset, use form reset
              const isReset = type === "reset";
              return (
                <Button
                  key={index}
                  type={type}
                  size={action.size || "default"}
                  variant={
                    ((variant === "icon" ? undefined : variant) as
                      | "link"
                      | "default"
                      | "destructive"
                      | "outline"
                      | "secondary"
                      | "ghost"
                      | undefined) || "default"
                  }
                  disabled={disabled || loading}
                  onClick={
                    type === "submit"
                      ? undefined // Let form handle submit
                      : isReset
                        ? handleReset
                        : (e) => {
                            e.preventDefault();
                            actionFn?.();
                          }
                  }
                  {...rest}
                >
                  {loading ? (
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  ) : React.isValidElement(icon) ? (
                    React.cloneElement(
                      icon as React.ReactElement,
                      {
                        className: `w-4 h-4 ${rest.color || ""} mr-2`,
                      } as Record<string, unknown>,
                    )
                  ) : icon == null ? null : (
                    <span className="text-xs text-red-500 mr-2">
                      Invalid icon prop
                    </span>
                  )}
                  {title}
                </Button>
              );
            })}
          </div>
        )}
      </section>
    </form>
  );
};

export default DynamicForm;
